import { indexOf } from 'lodash';
import createCachedSelector from 're-reselect';
import { compose } from 'redux';

import { ImageGalleryItemData } from '../../components/ImageGallery/ImageGalleryItem/ImageGalleryItem';
import { BannerId, ImageItem } from '../../typings/banners';
import {
    EditedBannerImageGallery,
    getEditedBannerWithImage,
    MassActionId
} from '../../typings/bannersEditState';
import { State } from '../../typings/state';

import { getEditedBannerCacheKey, selectEditedBanner } from './banner';

export const selectEditedBannerImageGallery = (
    state: State,
    bannerId: BannerId
): EditedBannerImageGallery => {
    const imageGallery = state.imageEditor[bannerId].imageGallery || {};
    if (bannerId === MassActionId.bannersEdit) {
        return imageGallery;
    }

    const banner = getEditedBannerWithImage(
        selectEditedBanner(state, bannerId)
    );
    if (!banner) {
        throw new Error(
            `failed to select image gallery for banner ${bannerId}: invalid type`
        );
    }
    return imageGallery;
};

const imageGalleryProp = <K extends keyof EditedBannerImageGallery>(
    propsKey: K
): ((state: State, bannerId: BannerId) => EditedBannerImageGallery[K]) => {
    return compose(
        x => x[propsKey],
        selectEditedBannerImageGallery
    );
};

export const selectBannerImagesForGallery = createCachedSelector(
    imageGalleryProp('images'),
    imageGalleryProp('selectedImageHashFromGallery'),
    (images, imagesHash): Array<ImageGalleryItemData> | undefined => {
        return images !== undefined
            ? images.map((image: ImageItem) => {
                  return {
                      image: {
                          ...image
                      },
                      isChecked: indexOf(imagesHash, image.hash) !== -1
                  } as ImageGalleryItemData;
              })
            : undefined;
    }
)(getEditedBannerCacheKey);

export const selectBannerImageFromGallery = createCachedSelector(
    selectEditedBannerImageGallery,
    selectionInfo => {
        if (!selectionInfo.images) {
            throw new Error('Could not select images!');
        }

        return selectionInfo.images.filter(image => {
            return selectionInfo.selectedImageHashFromGallery.includes(
                image.hash
            );
        });
    }
)(getEditedBannerCacheKey);
